import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { StreamChat } from "stream-chat";
import { useUser } from "../pages/login/UserContextPersistent";
import { useSettings } from "./SettingsContext";
import {
  bootstrapChatSession,
  createDirectChat,
  fetchChatMembers,
} from "../services/chatApi";
import {
  activeChatPreferences,
  resolveGroupChatTitle,
} from "../components/chat/chatPreferences";

const ChatContext = createContext(null);
const SYSTEM_USER_ID = "routeeye-system";
export const GROUP_THREAD_ID = "group";

function visibleMessages(messages) {
  return (messages || []).filter((message) => message.type !== "system");
}

function lastVisibleMessage(messages) {
  const list = visibleMessages(messages);
  return list[list.length - 1] || null;
}

function otherMemberFromChannel(channel, myId) {
  const members = Object.values(channel?.state?.members || {});
  const other = members.find((member) => {
    const id = String(member.user?.id || member.user_id || "");
    return id && id !== String(myId) && id !== SYSTEM_USER_ID;
  });
  return other?.user || { id: other?.user_id, name: other?.user_id };
}

function previewFromMessage(message) {
  if (!message) {
    return "No messages yet";
  }
  const text = String(message.text || "").trim();
  return text || "New message";
}

export function ChatProvider({ children }) {
  const { state, organizationInactive } = useUser();
  const settingsContext = useSettings();
  const appSettings = settingsContext?.settings;
  const clientRef = useRef(null);
  const groupChannelRef = useRef(null);
  const connectPromiseRef = useRef(null);
  const currentUserIdRef = useRef(null);

  const [isInboxOpen, setIsInboxOpen] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [groupChannel, setGroupChannel] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const [dmChannels, setDmChannels] = useState([]);
  const [members, setMembers] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [inboxTick, setInboxTick] = useState(0);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [chatDisplayName, setChatDisplayName] = useState("");
  const chatPrefsRef = useRef({
    displayName: "",
    opensOnOrgThread: false,
    storedName: "",
  });

  const isLoggedIn = Boolean(state?.isAuthenticated && state?.user?.id);
  const features = state?.features || [];
  const canOpenChat = features.some(
    (feature) => feature.api_path === "/api/chat" && feature.action === "GET",
  );
  const canSendChat = features.some(
    (feature) => feature.api_path === "/api/chat" && feature.action === "POST",
  );
  const chatEnabled = isLoggedIn && !organizationInactive && canOpenChat;

  const bumpInbox = useCallback(() => {
    setInboxTick((value) => value + 1);
  }, []);

  const rememberChatPreferences = useCallback((sessionChannel) => {
    const preferences = activeChatPreferences(
      {
        name: sessionChannel?.name || chatPrefsRef.current.storedName,
        displayName:
          sessionChannel?.displayName ?? chatPrefsRef.current.displayName,
        opensOnOrgThread:
          sessionChannel?.opensOnOrgThread ??
          chatPrefsRef.current.opensOnOrgThread,
      },
      {
        getItem(key) {
          if (key !== "appSettings" || !appSettings) {
            return null;
          }
          return JSON.stringify(appSettings);
        },
      },
    );
    chatPrefsRef.current = {
      displayName: preferences.displayName,
      opensOnOrgThread: preferences.opensOnOrgThread,
      storedName: sessionChannel?.name || chatPrefsRef.current.storedName,
    };
    setChatDisplayName(preferences.displayName);
    return preferences;
  }, [appSettings]);

  useEffect(() => {
    if (!appSettings) {
      return;
    }
    rememberChatPreferences({
      name: groupChannel?.data?.name || chatPrefsRef.current.storedName,
      displayName: chatPrefsRef.current.displayName,
      opensOnOrgThread: chatPrefsRef.current.opensOnOrgThread,
    });
  }, [
    appSettings,
    groupChannel,
    rememberChatPreferences,
  ]);

  const groupChatTitle = useCallback(
    (storedName) =>
      resolveGroupChatTitle({
        displayName: chatDisplayName,
        storedName: storedName || chatPrefsRef.current.storedName,
      }),
    [chatDisplayName],
  );

  const upsertInboxChannel = useCallback((channel) => {
    if (!channel?.id || channel.type !== "messaging") {
      return;
    }
    setDmChannels((current) => {
      const exists = current.find((item) => item.id === channel.id);
      if (exists) {
        return current.map((item) => (item.id === channel.id ? channel : item));
      }
      return [channel, ...current];
    });
    bumpInbox();
  }, [bumpInbox]);

  const refreshUnread = useCallback(() => {
    const client = clientRef.current;
    if (!client?.user) {
      setUnreadCount(0);
      return;
    }
    setUnreadCount(Number(client.user.total_unread_count || 0));
    bumpInbox();
  }, [bumpInbox]);

  const disconnectClient = useCallback(async () => {
    const client = clientRef.current;
    groupChannelRef.current = null;
    connectPromiseRef.current = null;
    currentUserIdRef.current = null;
    setGroupChannel(null);
    setGroupMessages([]);
    setDmChannels([]);
    setMembers([]);
    setUnreadCount(0);
    setCurrentUser(null);
    setChatDisplayName("");
    chatPrefsRef.current = {
      displayName: "",
      opensOnOrgThread: false,
      storedName: "",
    };
    setIsInboxOpen(false);
    setActiveThreadId(null);
    setError("");
    clientRef.current = null;

    if (client) {
      try {
        await client.disconnectUser();
      } catch (disconnectError) {
        console.warn("[StreamChat] disconnect failed:", disconnectError);
      }
    }
  }, []);

  const loadDirectChannels = useCallback(async (client, userId) => {
    const channels = await client.queryChannels(
      {
        type: "messaging",
        members: { $in: [String(userId)] },
      },
      { last_message_at: -1 },
      { limit: 30, watch: true, state: true },
    );
    setDmChannels(channels);
    bumpInbox();
    return channels;
  }, [bumpInbox]);

  const connectIfNeeded = useCallback(async () => {
    if (!chatEnabled) {
      throw new Error("Chat is not available");
    }
    if (clientRef.current?.userID) {
      return clientRef.current;
    }
    if (connectPromiseRef.current) {
      return connectPromiseRef.current;
    }

    setConnecting(true);
    setError("");
    connectPromiseRef.current = (async () => {
      const session = await bootstrapChatSession();
      const client = StreamChat.getInstance(session.apiKey);
      await client.connectUser(
        {
          id: session.userId,
          name: session.name,
        },
        session.token,
      );
      clientRef.current = client;
      currentUserIdRef.current = session.userId;
      setCurrentUser({
        id: session.userId,
        name: session.name,
        userName: session.userName,
      });

      const channel = client.channel(
        session.groupChannel.type,
        session.groupChannel.id,
      );
      await channel.watch();
      groupChannelRef.current = channel;
      setGroupChannel(channel);
      setGroupMessages(Array.from(channel.state.messages || []));
      rememberChatPreferences(session.groupChannel);
      await loadDirectChannels(client, session.userId);
      refreshUnread();
      return client;
    })();

    try {
      return await connectPromiseRef.current;
    } catch (connectError) {
      connectPromiseRef.current = null;
      const message =
        connectError?.response?.data?.error ||
        connectError?.message ||
        "Unable to open chat";
      setError(message);
      throw connectError;
    } finally {
      setConnecting(false);
    }
  }, [chatEnabled, loadDirectChannels, refreshUnread, rememberChatPreferences]);

  useEffect(() => {
    if (!chatEnabled) {
      disconnectClient();
      return undefined;
    }
    connectIfNeeded().catch(() => {});
    return undefined;
  }, [chatEnabled, connectIfNeeded, disconnectClient]);

  useEffect(() => {
    const client = clientRef.current;
    if (!client) {
      return undefined;
    }

    const handleEvent = async (event) => {
      if (event.total_unread_count !== undefined) {
        setUnreadCount(Number(event.total_unread_count || 0));
      }

      const watchedGroup = groupChannelRef.current;
      if (watchedGroup && event.cid === watchedGroup.cid && event.message) {
        setGroupMessages(Array.from(watchedGroup.state.messages || []));
        bumpInbox();
      }

      const eventChannel = event.channel || event.channel_id
        ? client.channel(
            event.channel?.type || event.channel_type || "messaging",
            event.channel?.id || event.channel_id,
          )
        : null;

      const isDirect =
        event.channel_type === "messaging" ||
        eventChannel?.type === "messaging" ||
        String(event.cid || "").startsWith("messaging:");

      if (
        isDirect &&
        event.message &&
        event.type !== "health.check" &&
        (event.type === "message.new" ||
          event.type === "notification.message_new" ||
          event.type === "notification.added_to_channel")
      ) {
        try {
          const channel =
            eventChannel ||
            (event.cid ? client.channel(event.cid.split(":")[0], event.cid.split(":")[1]) : null);
          if (channel) {
            await channel.watch();
            upsertInboxChannel(channel);
          }
        } catch (watchError) {
          console.warn("[StreamChat] incoming DM watch failed:", watchError);
        }
      }

      bumpInbox();
    };

    client.on(handleEvent);
    return () => {
      client.off(handleEvent);
    };
  }, [currentUser, upsertInboxChannel, bumpInbox]);

  const inboxThreads = useMemo(() => {
    const myId = currentUserIdRef.current;
    const groupLast = lastVisibleMessage(groupMessages);
    const groupUnread = Number(
      groupChannel?.countUnread?.() || groupChannel?.state?.unreadCount || 0,
    );
    const groupThread = {
      id: GROUP_THREAD_ID,
      kind: "group",
      title: groupChatTitle(groupChannel?.data?.name),
      preview: previewFromMessage(groupLast),
      lastMessageAt: groupLast?.created_at || groupChannel?.data?.last_message_at || null,
      unread: groupUnread,
    };

    const dmThreads = dmChannels.map((channel) => {
      const other = otherMemberFromChannel(channel, myId);
      const last = lastVisibleMessage(channel.state?.messages);
      return {
        id: channel.id,
        kind: "dm",
        title: other?.name || other?.id || "Direct message",
        preview: previewFromMessage(last),
        lastMessageAt: last?.created_at || channel.data?.last_message_at || channel.state?.last_message_at,
        unread: Number(channel.countUnread?.() || channel.state?.unreadCount || 0),
        member: other,
        channel,
      };
    });

    dmThreads.sort((left, right) => {
      const leftTime = left.lastMessageAt ? new Date(left.lastMessageAt).getTime() : 0;
      const rightTime = right.lastMessageAt ? new Date(right.lastMessageAt).getTime() : 0;
      return rightTime - leftTime;
    });

    return [
      groupThread,
      ...dmThreads.filter((thread) => thread.lastMessageAt || thread.unread > 0),
    ];
  }, [dmChannels, groupChannel, groupMessages, inboxTick, groupChatTitle]);

  const toggleGroupChat = useCallback(async () => {
    if (!chatEnabled) {
      return;
    }
    if (isInboxOpen) {
      setIsInboxOpen(false);
      setActiveThreadId(null);
      return;
    }
    await connectIfNeeded();
    if (currentUserIdRef.current && clientRef.current) {
      await loadDirectChannels(clientRef.current, currentUserIdRef.current);
    }
    refreshUnread();
    const preferences = rememberChatPreferences({
      name: groupChannelRef.current?.data?.name || chatPrefsRef.current.storedName,
      displayName: chatPrefsRef.current.displayName,
      opensOnOrgThread: chatPrefsRef.current.opensOnOrgThread,
    });
    if (preferences.opensOnOrgThread) {
      setIsInboxOpen(true);
      setActiveThreadId(GROUP_THREAD_ID);
      const channel = groupChannelRef.current;
      if (channel) {
        await channel.watch();
        setGroupMessages(Array.from(channel.state.messages || []));
        await channel.markRead();
      }
      refreshUnread();
      return;
    }
    setActiveThreadId(null);
    setIsInboxOpen(true);
  }, [
    chatEnabled,
    connectIfNeeded,
    isInboxOpen,
    loadDirectChannels,
    refreshUnread,
    rememberChatPreferences,
  ]);

  const openThread = useCallback(async (threadId) => {
    await connectIfNeeded();
    setIsInboxOpen(true);
    setActiveThreadId(threadId);

    if (threadId === GROUP_THREAD_ID) {
      const channel = groupChannelRef.current;
      if (channel) {
        await channel.watch();
        setGroupMessages(Array.from(channel.state.messages || []));
        await channel.markRead();
      }
      refreshUnread();
      return;
    }

    const existing = dmChannels.find((channel) => channel.id === threadId);
    const channel = existing || clientRef.current?.channel("messaging", threadId);
    if (channel) {
      await channel.watch();
      upsertInboxChannel(channel);
      await channel.markRead();
    }
    refreshUnread();
  }, [connectIfNeeded, dmChannels, refreshUnread, upsertInboxChannel]);

  const closeActiveThread = useCallback(() => {
    setActiveThreadId(null);
    refreshUnread();
  }, [refreshUnread]);

  const sendGroupMessage = useCallback(async (text) => {
    if (!canSendChat) {
      return;
    }
    const body = String(text || "").trim();
    if (!body || !groupChannelRef.current) {
      return;
    }
    await groupChannelRef.current.sendMessage({ text: body });
    setGroupMessages(Array.from(groupChannelRef.current.state.messages || []));
    bumpInbox();
  }, [bumpInbox, canSendChat]);

  const loadMembers = useCallback(async () => {
    const list = await fetchChatMembers();
    setMembers(list);
    return list;
  }, []);

  const openDirectChat = useCallback(
    async (userId, displayName) => {
      if (!canSendChat) {
        return;
      }
      await connectIfNeeded();
      const channelInfo = await createDirectChat(userId);
      const client = clientRef.current;
      const channel = client.channel(channelInfo.type, channelInfo.id);
      await channel.watch();
      upsertInboxChannel(channel);
      await channel.markRead();
      refreshUnread();
      setIsInboxOpen(true);
      setActiveThreadId(channelInfo.id);
      return displayName;
    },
    [canSendChat, connectIfNeeded, refreshUnread, upsertInboxChannel],
  );

  const sendDirectMessage = useCallback(async (channelId, text) => {
    if (!canSendChat) {
      return;
    }
    const body = String(text || "").trim();
    if (!body) {
      return;
    }

    const existing = dmChannels.find((item) => item.id === channelId);
    const channel = existing || clientRef.current?.channel("messaging", channelId);
    if (!channel) {
      return;
    }

    await channel.sendMessage({ text: body });
    upsertInboxChannel(channel);
    bumpInbox();
  }, [bumpInbox, canSendChat, dmChannels, upsertInboxChannel]);

  const activeThread = useMemo(() => {
    if (!activeThreadId) {
      return null;
    }
    if (activeThreadId === GROUP_THREAD_ID) {
      return {
        id: GROUP_THREAD_ID,
        kind: "group",
        title: groupChatTitle(groupChannel?.data?.name),
        messages: visibleMessages(groupMessages),
      };
    }
    const channel = dmChannels.find((item) => item.id === activeThreadId);
    const other = channel
      ? otherMemberFromChannel(channel, currentUserIdRef.current)
      : null;
    return {
      id: activeThreadId,
      kind: "dm",
      title: other?.name || other?.id || "Direct message",
      messages: visibleMessages(channel?.state?.messages),
      member: other,
    };
  }, [activeThreadId, dmChannels, groupChannel, groupMessages, inboxTick, groupChatTitle]);

  const value = useMemo(
    () => ({
      chatEnabled,
      canSendChat,
      connecting,
      error,
      currentUser,
      isInboxOpen,
      activeThreadId,
      activeThread,
      inboxThreads,
      groupChannel,
      groupMessages,
      members,
      unreadCount,
      toggleGroupChat,
      openThread,
      closeActiveThread,
      sendGroupMessage,
      openDirectChat,
      sendDirectMessage,
      loadMembers,
    }),
    [
      chatEnabled,
      canSendChat,
      connecting,
      error,
      currentUser,
      isInboxOpen,
      activeThreadId,
      activeThread,
      inboxThreads,
      groupChannel,
      groupMessages,
      members,
      unreadCount,
      toggleGroupChat,
      openThread,
      closeActiveThread,
      sendGroupMessage,
      openDirectChat,
      sendDirectMessage,
      loadMembers,
    ],
  );

  return (
    <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    return {
      chatEnabled: false,
      unreadCount: 0,
      toggleGroupChat: () => {},
    };
  }
  return context;
}
